import { ref, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { addDays, isValid, differenceInDays, format } from 'date-fns';
import useHttp from '@/utils/httpClient';
import { useRoute } from 'vue-router';
import router from '@/router';
import { useAppConfigStore } from '@stores/appConfig.store';

const http = useHttp();

function parseChildrenAgesFromQuery(value) {
  if (!value) return null;
  const raw = Array.isArray(value) ? value[value.length - 1] : value;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export const useBookingQueryStore = defineStore('bookingQuery', () => {
  const appConfigStore = useAppConfigStore();
  const { applicationSetup } = storeToRefs(appConfigStore);
  const route = useRoute();
  const occupancyRoomSettings = ref({});
  const hotelPolicies = ref({});

  const activatedPromotionalCodes = ref([]);

  const userDirectIncentive = ref('');

  const query = ref({
    startDate: '',
    endDate: '',
    occupancy: [],
    promotion_code: '',
    campaign_code: '',
    referrer: '',
    filterOffers: '',
  });

  const showMaxNightsModal = ref(false);
  const pendingMaxNightsModal = ref(false);

  // Mark that we want to show modal when app is ready
  const requestMaxNightsModal = () => {
    if (showMaxNightsModal.value) return;
    pendingMaxNightsModal.value = true;

    // If app is already loaded, show modal immediately
    const { setupLoaded } = storeToRefs(appConfigStore);
    if (setupLoaded.value) {
      showPendingMaxNightsModal();
    }
  };

  // Show modal when app is ready
  const showPendingMaxNightsModal = () => {
    if (pendingMaxNightsModal.value) {
      showMaxNightsModal.value = true;
      pendingMaxNightsModal.value = false;
    }
  };

  // Show modal immediately (for calendar selections)
  const showMaxNightsModalNow = () => {
    if (showMaxNightsModal.value) return;
    showMaxNightsModal.value = true;
  };

  const setPromotionalCodeOnQuery = () => {
    let promoCodes = [];
    let campaignCode = '';
    activatedPromotionalCodes.value.forEach((code) => {
      if (code.isCampaignCode) {
        campaignCode = code.value;
      } else {
        promoCodes.push(code.value);
      }
    });
    query.value.promotion_code = JSON.stringify(promoCodes);
    query.value.campaign_code = campaignCode;
  };

  const loadPromotionsFromSession = () => {
    const savedPromotionalCodes = sessionStorage.getItem(
      'activatedPromotionalCodes' + applicationSetup.value.hotel_id,
    );
    if (savedPromotionalCodes) {
      activatedPromotionalCodes.value = JSON.parse(savedPromotionalCodes);
    }
  };

  const saveToSession = () => {
    sessionStorage.setItem(
      'activatedPromotionalCodes' + applicationSetup.value.hotel_id,
      JSON.stringify(activatedPromotionalCodes.value),
    );
  };

  const setInitialQuery = (setup) => {
    loadPromotionsFromSession();
    let defaultCheckinDate = new Date();
    if (
      setup.default_checkin_date &&
      setup.default_checkin_date != '0000-00-00'
    ) {
      defaultCheckinDate = new Date(setup.default_checkin_date + 'T00:00:00');
    }
    const endDate = addDays(
      defaultCheckinDate,
      parseInt(setup.default_nr_nights),
    );

    let defaultNrRooms = 1;
    if (setup.rooms) {
      defaultNrRooms = parseInt(setup.rooms);
    }
    let defaultNrAdults = 2;
    if (setup.occupancy.default_adults) {
      defaultNrAdults = parseInt(setup.occupancy.default_adults);
    }

    let occupancy = [];
    for (let i = 0; i < defaultNrRooms; i++) {
      occupancy.push({
        nrAdults: defaultNrAdults,
        nrChildren: 0,
        nrBabies: 0,
      });
    }

    let newQuery = {
      startDate: defaultCheckinDate,
      endDate: endDate,
      occupancy: occupancy,
      referrer: document.referrer,
    };

    newQuery = getQueryParams(newQuery, setup);

    query.value = newQuery;
    setPromotionalCodeOnQuery();
    occupancyRoomSettings.value = setup.occupancy;
    hotelPolicies.value = setup.hotel_policies || {};
  };

  const setStartDate = (newStartDate) => {
    query.value.startDate = newStartDate;
  };

  const setEndDate = (newEndDate) => {
    const MAX_NIGHTS = 90;
    if (query.value.startDate && newEndDate) {
      const nights = differenceInDays(newEndDate, query.value.startDate);
      if (nights > MAX_NIGHTS) {
        newEndDate = addDays(query.value.startDate, MAX_NIGHTS);
      }
    }
    query.value.endDate = newEndDate;
  };

  const updateOccupancy = (occupancy) => {
    query.value.occupancy = occupancy;
  };

  const getQueryParams = (query, setup, options = {}) => {
    const { skipNavigation = false } = options;
    const clonedRoute = { ...route.query };
    delete clonedRoute.promotion;
    delete clonedRoute.room;
    delete clonedRoute.preselect;
    delete clonedRoute.select;

    if (
      !skipNavigation &&
      (route.query.promotion || route.query.preselect || route.query.select) &&
      !route.path.includes('/rate/details/')
    ) {
      let rateId = '';
      if (route.query.promotion && route.query.promotion.includes('promo')) {
        const promotionID = route.query.promotion.replace('promo', '');
        rateId = promotionID;
        if (route.query.room) {
          const roomID = route.query.room.replace('room', '');
          rateId = promotionID + '.' + roomID;
        }
      }

      const offerFormat = /^p\d+r\d+$/;
      if (
        route.query.preselect &&
        (offerFormat.test(route.query.preselect) ||
          route.query.preselect.includes('promo'))
      ) {
        if (route.query.preselect.includes('promo')) {
          rateId = route.query.preselect.replace('promo', '');
        }
        if (offerFormat.test(route.query.preselect)) {
          let codes = route.query.preselect.split('r');
          const promotionID = codes[0].replace('p', '');
          rateId = promotionID + '.' + codes[1];
        }
      }

      if (route.query.select) {
        if (route.query.select.startsWith('p')) {
          const selectValue = route.query.select.replace('p', '');

          if (selectValue.includes('r')) {
            // Format: p32r5 (rate + room)
            const codes = selectValue.split('r');
            rateId = codes[0] + '.' + codes[1];
          } else {
            // Format: p32 (rate only)
            rateId = selectValue;
          }
        }
      }

      if (rateId) {
        router.push({
          name: 'rate details',
          params: { id: rateId },
          query: clonedRoute,
        });
      }
    }

    if (
      !skipNavigation &&
      route.query.room &&
      route.query.room.includes('room') &&
      !(route.query.promotion || route.query.preselect || route.query.select) &&
      !route.path.includes('/room/details/')
    ) {
      const roomID = route.query.room.replace('room', '');
      router.push({
        name: 'room details',
        params: { id: roomID },
        query: clonedRoute,
      });
    }

    if (route.query.startDay) {
      // If startDay is an array, get the last value
      let startDayValue = route.query.startDay;
      if (Array.isArray(startDayValue)) {
        startDayValue = startDayValue[startDayValue.length - 1];
      }
      let dateParts = startDayValue.split('-');
      if (dateParts.length === 1) {
        dateParts = startDayValue.split('/');
      }
      // Safety check: only proceed if we have 3 valid parts (prevents padStart error with startDay=0)
      if (
        dateParts.length === 3 &&
        dateParts[0] &&
        dateParts[1] &&
        dateParts[2]
      ) {
        let queryDate;
        if (dateParts[0].length === 4) {
          let year = dateParts[0];
          let month = dateParts[1].padStart(2, '0');
          let day = dateParts[2].padStart(2, '0');
          queryDate = new Date(year + '-' + month + '-' + day + 'T00:00:00');
        } else {
          queryDate = new Date(
            dateParts[2] +
              '-' +
              dateParts[1].padStart(2, '0') +
              '-' +
              dateParts[0].padStart(2, '0') +
              'T00:00:00',
          );
        }

        if (isValid(queryDate)) {
          query.startDate = queryDate;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const formattedToday = format(today, 'yyyy-MM-dd');
          const minStartDay =
            setup.default_checkin_date &&
            setup.default_checkin_date != '0000-00-00' &&
            setup.default_checkin_date > formattedToday
              ? setup.default_checkin_date
              : formattedToday;
          if (format(query.startDate, 'yyyy-MM-dd') < minStartDay) {
            query.startDate = new Date(minStartDay + 'T00:00:00');
          }
          let nrDays = setup.default_nr_nights;
          let wasAdjusted = false;

          if (route.query.nrNights) {
            let nrNightsValue = route.query.nrNights;
            if (Array.isArray(nrNightsValue)) {
              nrNightsValue = nrNightsValue[nrNightsValue.length - 1];
            }
            const originalNrDays = parseInt(nrNightsValue);
            nrDays = originalNrDays;
            const MAX_NIGHTS = 90;
            if (nrDays > MAX_NIGHTS) {
              nrDays = MAX_NIGHTS;
              wasAdjusted = true;
            }
          }
          if (query.startDate) {
            query.endDate = addDays(query.startDate, nrDays);

            if (wasAdjusted) {
              const updatedQuery = { ...route.query, nrNights: nrDays };
              router.replace({ query: updatedQuery });
              // Request modal to be shown when app is ready
              requestMaxNightsModal();
            }
          }
        }
      }
    }

    if (
      route.query.amount ||
      route.query.nrAdults ||
      route.query.nrChildren ||
      route.query.childrenAges ||
      route.query.nrBabies
    ) {
      const amount =
        route.query.amount && route.query.amount > 0
          ? parseInt(route.query.amount)
          : parseInt(setup.rooms);
      let nrAdults =
        route.query.nrAdults && route.query.nrAdults > 0
          ? parseInt(route.query.nrAdults)
          : parseInt(setup.occupancy.default_adults);
      let occupancies = [];

      let childrenAges = parseChildrenAgesFromQuery(route.query.childrenAges);

      let nrChildren =
        route.query.nrChildren && route.query.nrChildren > 0
          ? parseInt(route.query.nrChildren)
          : 0;

      if (nrChildren > setup.occupancy.max_children) {
        nrChildren = setup.occupancy.max_children;
      }

      if (!setup.hotel_policies?.children_accepted) {
        nrChildren = 0;
      }

      let nrBabies =
        route.query.nrBabies && route.query.nrBabies > 0
          ? parseInt(route.query.nrBabies)
          : 0;

      if (!setup.hotel_policies?.babies_accepted) {
        nrBabies = 0;
      }

      const minChildrenAge = setup.occupancy.min_children_ages;
      const maxChildrenAge = setup.occupancy.max_children_ages;

      let finalChildrenAges = [];

      if (nrChildren > 0) {
        if (!childrenAges || childrenAges.length === 0) {
          finalChildrenAges = Array(nrChildren).fill(minChildrenAge);
        } else {
          finalChildrenAges = childrenAges.slice(0, nrChildren).map((age) => {
            if (age < minChildrenAge || age > maxChildrenAge) {
              return minChildrenAge;
            }
            return age;
          });

          if (finalChildrenAges.length < nrChildren) {
            const missing = nrChildren - finalChildrenAges.length;
            finalChildrenAges = [
              ...finalChildrenAges,
              ...Array(missing).fill(minChildrenAge),
            ];
          }
        }
      }

      for (let i = 0; i < amount; i++) {
        occupancies.push({
          nrAdults: nrAdults,
          nrChildren: nrChildren,
          nrBabies: nrBabies,
          children_ages: finalChildrenAges,
        });
      }

      query.occupancy = occupancies;
    }

    if (route.query.occupancies) {
      let occupancy = JSON.parse(route.query.occupancies);
      let occupancies = [];
      for (let i = 0; i < occupancy.length; i++) {
        occupancies.push({
          nrAdults: parseInt(occupancy[i].adults),
          nrChildren: parseInt(occupancy[i].children),
          nrBabies: parseInt(occupancy[i].babies),
          children_ages: occupancy[i].children_ages || [],
        });
      }
      query.occupancy = occupancies;
    }

    if (route.query.url) {
      query.referrer = route.query.url;
    }
    if (route.query.r) {
      query.referrer = route.query.r;
    }

    if (route.query.pc) {
      const promoCode = route.query.pc.toLowerCase();
      const existingCode = activatedPromotionalCodes.value.find(
        (code) => code.value === promoCode,
      );
      if (!existingCode && !wouldAddingPromoExceedLimit(promoCode)) {
        checkCampaignCode(promoCode).then((response) => {
          if (
            response &&
            (response.isCampaignCodeCodeValid ||
              response.isPromoCodeCodeValid ||
              response.isSpecialRateCodeCodeValid)
          ) {
            activatedPromotionalCodes.value.push({
              value: promoCode,
              isCampaignCode: response.isCampaignCodeCodeValid || false,
            });
            saveToSession();
            setPromotionalCodeOnQuery();
          }
        });
      }
    }

    if (
      route.query.filterOffers &&
      (route.query.filterOffers === 'all' ||
        route.query.filterOffers === 'promotions')
    ) {
      query.filterOffers = route.query.filterOffers;
    }

    return query;
  };

  const userOccupancyTotals = computed(() => {
    return query.value.occupancy.reduce(
      (acc, curr) => {
        acc.totalRooms += 1;
        acc.totalAdults += curr.nrAdults;
        acc.totalChildren += curr.nrChildren;
        acc.totalBabies += curr.nrBabies;
        return acc;
      },
      { totalRooms: 0, totalAdults: 0, totalChildren: 0, totalBabies: 0 },
    );
  });

  const updateUserSpecialRatesDetails = (userCodes) => {
    activatedPromotionalCodes.value = userCodes;
    setPromotionalCodeOnQuery();
    saveToSession();
  };

  // Single source of truth: backend limit for promotion_code JSON string total length
  const PROMOTION_CODE_MAX_LENGTH = 45;

  const wouldAddingPromoExceedLimit = (newPromoCode) => {
    const promoCodes = activatedPromotionalCodes.value
      .filter((c) => !c.isCampaignCode)
      .map((c) => c.value);
    promoCodes.push(newPromoCode);
    return JSON.stringify(promoCodes).length > PROMOTION_CODE_MAX_LENGTH;
  };

  const checkCampaignCode = async (campaignCode) => {
    const params = {
      campaign_code: campaignCode,
    };

    return new Promise((resolve, reject) => {
      http
        .get('check_campaigncode', params)
        .then((rsp) => {
          const responseContext = rsp.data.response;
          resolve(responseContext);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const removePromoCode = (value) => {
    activatedPromotionalCodes.value = activatedPromotionalCodes.value.filter(
      (code) => code.value !== value,
    );
    setPromotionalCodeOnQuery();
    saveToSession();

    if (route.query.pc && route.query.pc.toLowerCase() === value) {
      const query = { ...route.query };
      delete query.pc;
      router.replace({ query });
    }
  };

  const resetPromotionalCodes = () => {
    activatedPromotionalCodes.value = [];
    setPromotionalCodeOnQuery();
    saveToSession();
  };

  const OCCUPANCY_URL_KEYS = [
    'occupancies',
    'nrAdults',
    'nrChildren',
    'nrBabies',
    'childrenAges',
    'amount',
  ];

  const stripOccupancyFromUrlQuery = (urlQuery) => {
    const next = { ...urlQuery };
    OCCUPANCY_URL_KEYS.forEach((key) => {
      delete next[key];
    });
    return next;
  };

  const buildUrlQueryFromBookingQuery = (options = {}) => {
    const { includePromo = true } = options;
    const params = {};
    const q = query.value;

    if (q.startDate && isValid(new Date(q.startDate))) {
      params.startDay = format(new Date(q.startDate), 'yyyy-MM-dd');
    }

    if (
      q.startDate &&
      q.endDate &&
      isValid(new Date(q.startDate)) &&
      isValid(new Date(q.endDate))
    ) {
      const startDate = new Date(q.startDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(q.endDate);
      endDate.setHours(0, 0, 0, 0);
      let nights = differenceInDays(endDate, startDate);
      if (nights === 0) nights = 1;
      params.nrNights = String(nights);
    }

    if (includePromo) {
      const firstPromo = activatedPromotionalCodes.value[0];
      if (firstPromo?.value) {
        params.pc = firstPromo.value;
      }
    }

    return params;
  };

  const applyBookingParamsFromRoute = (setup) => {
    if (!setup) return;
    const updated = getQueryParams({ ...query.value }, setup, {
      skipNavigation: true,
    });
    query.value = updated;
    setPromotionalCodeOnQuery();
  };

  const daysDifference = computed(() => {
    if (!isValid(new Date(query.value.startDate))) {
      return 0;
    }

    // If no endDate is provided, assume 1 night (single day selection)
    if (!query.value.endDate || !isValid(new Date(query.value.endDate))) {
      return 1;
    }

    // Normalize dates to midnight to avoid timezone issues
    const startDate = new Date(query.value.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(query.value.endDate);
    endDate.setHours(0, 0, 0, 0);

    const diff = differenceInDays(endDate, startDate);

    // If same day selected, show 1 night
    return diff === 0 ? 1 : diff;
  });

  return {
    query,
    userOccupancyTotals,
    occupancyRoomSettings,
    hotelPolicies,
    userDirectIncentive,
    showMaxNightsModal,
    requestMaxNightsModal,
    showPendingMaxNightsModal,
    showMaxNightsModalNow,
    setInitialQuery,
    setStartDate,
    setEndDate,
    updateOccupancy,
    updateUserSpecialRatesDetails,
    checkCampaignCode,
    wouldAddingPromoExceedLimit,
    PROMOTION_CODE_MAX_LENGTH,
    activatedPromotionalCodes,
    removePromoCode,
    daysDifference,
    loadPromotionsFromSession,
    resetPromotionalCodes,
    buildUrlQueryFromBookingQuery,
    stripOccupancyFromUrlQuery,
    applyBookingParamsFromRoute,
  };
});
